import React, { useEffect, useState } from "react";
import {
  Form,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  useCities,
  useCountries,
  useLocalities,
  useProvinces,
} from "../../Hooks/use-locations";
import { useDataFetch } from "../../Hooks/Use-data-fetch";

// Recargo fijo sobre la tarifa del envío (también en reenvíos).
const SERVIENTREGA_SURCHARGE = 3100;
const INTERRAPIDISIMO_SURCHARGE = 1965;

const EMPTY_SHIPPING = {
  name: "",
  address: "",
  country_id: "",
  province_id: "",
  city_id: "",
  postal_code: "",
  neighborhood: "No aplica",
  neighborhood_code: "",
  observations: "",
  receiver: { type: "self", name: "", document: "", phone: "" },
};

const DeliveryAddressSelector = ({
  addresses = [],
  selectedAddress,
  setSelectedAddress,
  loadingAddresses,
  setShipping,
  selectedUser,
  setShippingCost,
  selectedProducts = [],
  shippingCost = 0,
  carrier,
  setCarrier,
  // Reenvío por devolución: sin cambiar de transportadora.
  carrierLocked = false,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState(false);

  const [newShipping, setNewShipping] = useState(EMPTY_SHIPPING);

  const { countries } = useCountries(selectedUser?.value);
  const { provinces } = useProvinces(newShipping.country_id);
  const { cities } = useCities(newShipping.province_id);
  const { localities, isLoading: loadingLocalities } = useLocalities(
    newShipping.province_id,
    newShipping.city_id
  );

  const { postData } = useDataFetch();

  // Regla de empaque de la transportadora: su mensaje se muestra tal cual.
  const ruleErrorOf = (response) =>
    response?.message === "shipping_rule_violation" ? response.errors : null;

  const quoteErrorText =
    typeof quoteError === "string"
      ? quoteError
      : "No fue posible calcular el costo del envío para este destino.";

  /**
   * Normaliza la respuesta de Interrapidísimo (array) al valor más barato.
   */
  const extractInterrapidisimoTotal = (response) => {
    const list = response?.carriers ?? response;

    if (!Array.isArray(list) || list.length === 0) {
      return NaN;
    }

    const cheapest = list.reduce((min, current) => {
      const currentValor = Number(current?.Precio?.Valor);
      const minValor = Number(min?.Precio?.Valor);

      if (!Number.isFinite(currentValor)) return min;
      if (!Number.isFinite(minValor)) return current;

      return currentValor < minValor ? current : min;
    });

    return Number(cheapest?.Precio?.Valor) + INTERRAPIDISIMO_SURCHARGE;
  };

  /**
   * Cotización Servientrega
   */
  const { mutate: calculateServientrega } = postData(
    "/order/shipping/carriers",
    {},
    {
      onSuccess: (response) => {
        setLoadingQuote(false);

        const quote = response?.carriers;
        const total = Number(quote?.ValorTotal);

        if (!quote || !Number.isFinite(total)) {
          setShippingCost(0);
          setQuoteError(ruleErrorOf(response) || true);
          return;
        }

        setQuoteError(false);
        setShippingCost(total + SERVIENTREGA_SURCHARGE);
      },

      onError: (error) => {
        console.error("Error consultando Servientrega:", error);
        setLoadingQuote(false);
        setShippingCost(0);
        setQuoteError(true);
      },
    }
  );

  /**
   * Cotización Interrapidísimo
   */
  const { mutate: calculateInterrapidisimo } = postData(
    "/order/shipping/carriers/interrapidisimo",
    {},
    {
      onSuccess: (response) => {
        setLoadingQuote(false);

        const raw = response?.carriers ?? response;
        const total = extractInterrapidisimoTotal(raw);

        if (!Number.isFinite(total)) {
          console.error(
            "Cotización de Interrapidísimo: respuesta sin valor",
            response
          );
          setShippingCost(0);
          setQuoteError(ruleErrorOf(response) || true);
          return;
        }

        setQuoteError(false);
        setShippingCost(total);
      },

      onError: (error) => {
        console.error("Error consultando Interrapidísimo:", error);
        setLoadingQuote(false);
        setShippingCost(0);
        setQuoteError(true);
      },
    }
  );

  const hasProducts = selectedProducts.length > 0;
  const hasCarrier = !!carrier;

  const getQuoteItems = () =>
    selectedProducts.map((item) => ({
      product_id: item.productId,
      quantity: Number(item.quantity || 0),
    }));

  /**
   * Cotiza el envío según la transportadora seleccionada.
   */
  const quoteShipping = (city, province, neighborhoodCode) => {
    if (!city || !province) {
      setLoadingQuote(false);
      setShippingCost(0);
      return;
    }

    const items = getQuoteItems();

    if (!items.length) {
      setLoadingQuote(false);
      setShippingCost(0);
      return;
    }

    if (carrier !== "servientrega" && carrier !== "interrapidisimo") {
      setLoadingQuote(false);
      setQuoteError(false);
      setShippingCost(0);
      return;
    }

    setLoadingQuote(true);
    setQuoteError(false);
    setShippingCost(0);

    // Barrio / corregimiento: Inter cotiza y valida cobertura con esa localidad.
    const payload = { items, city, province, neighborhood_code: neighborhoodCode || undefined };

    if (carrier === "servientrega") {
      calculateServientrega(payload);
    } else {
      calculateInterrapidisimo(payload);
    }
  };

  const getSavedAddressLocation = (id) => {
    const address = addresses.find((item) => item.id === id);

    if (!address) {
      return null;
    }

    return {
      city: address.city_id?.name,
      province: address.province_id?.name,
      neighborhood_code: address.neighborhood_code,
    };
  };

  const handleCarrierChange = (e) => {
    const value = e.target.value;

    setCarrier(value);
    setSelectedAddress(null);
    setShipping(undefined);
    setShowAddForm(false);
    setLoadingQuote(false);
    setQuoteError(false);
    setShippingCost(0);
  };

  const handleSelectSaved = (id) => {
    setSelectedAddress(id);
    setShipping(undefined);
    setShowAddForm(false);
    setQuoteError(false);
    setLoadingQuote(false);
    setShippingCost(0);

    if (carrier !== "servientrega" && carrier !== "interrapidisimo") {
      return;
    }

    const location = getSavedAddressLocation(id);

    if (!location?.city || !location?.province) {
      setQuoteError(true);
      return;
    }

    quoteShipping(location.city, location.province, location.neighborhood_code);
  };

  const updateShipping = (updated) => {
    setNewShipping(updated);
    setShipping(updated);
  };

  const handleReceiverChange = (field, value) =>
    updateShipping({
      ...newShipping,
      receiver: { ...newShipping.receiver, [field]: value },
    });

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedShipping = {
      ...newShipping,
      [name]: value,
    };

    // El select de barrio entrega el IdLocalidad; se guarda también el nombre.
    if (name === "neighborhood") {
      updatedShipping = {
        ...updatedShipping,
        neighborhood: localities.find((l) => l.id === value)?.name || "No aplica",
        neighborhood_code: value,
      };
    }

    if (name === "country_id") {
      updatedShipping = {
        ...updatedShipping,
        country_id: value,
        province_id: "",
        city_id: "",
        neighborhood: "No aplica",
        neighborhood_code: "",
      };
    }

    if (name === "province_id") {
      updatedShipping = {
        ...updatedShipping,
        province_id: value,
        city_id: "",
        neighborhood: "No aplica",
        neighborhood_code: "",
      };
    }

    // Los corregimientos se listan por municipio: otro municipio invalida el elegido.
    if (name === "city_id") {
      updatedShipping = {
        ...updatedShipping,
        neighborhood: "No aplica",
        neighborhood_code: "",
      };
    }

    updateShipping(updatedShipping);

    if (
      name === "country_id" ||
      name === "province_id" ||
      name === "city_id" ||
      name === "neighborhood"
    ) {
      setShippingCost(0);
      setQuoteError(false);
    }
  };

  const handleCalculateManualShipping = () => {
    if (carrier !== "servientrega" && carrier !== "interrapidisimo") {
      return;
    }

    if (!newShipping.city_id || !newShipping.province_id) {
      setQuoteError(true);
      setShippingCost(0);
      return;
    }

    const cityObj = cities.find(
      (city) => city.id === newShipping.city_id
    );

    const provinceObj = provinces.find(
      (province) => province.id === newShipping.province_id
    );

    if (!cityObj || !provinceObj) {
      setQuoteError(true);
      setShippingCost(0);
      return;
    }

    quoteShipping(cityObj.name, provinceObj.name, newShipping.neighborhood_code);
  };

  const handleFormToggle = () => {
    const nextValue = !showAddForm;

    setShowAddForm(nextValue);
    setSelectedAddress(null);
    setShipping(undefined);
    setQuoteError(false);
    setLoadingQuote(false);
    setShippingCost(0);

    if (!nextValue) {
      setNewShipping(EMPTY_SHIPPING);
    }
  };

  // Colombia por defecto al abrir el formulario manual
  useEffect(() => {
    if (!showAddForm || newShipping.country_id || !countries?.length) return;
    const colombia = countries.find((c) => c.name?.toLowerCase() === "colombia");
    if (colombia) updateShipping({ ...newShipping, country_id: colombia.country_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddForm, countries]);

  useEffect(() => {
    if (!hasProducts) {
      setCarrier("");
      setSelectedAddress(null);
      setShipping(undefined);
      setShowAddForm(false);
      setLoadingQuote(false);
      setQuoteError(false);
      setShippingCost(0);
    }
  }, [hasProducts]);

  if (!hasProducts) {
    return null;
  }

  const isQuoting =
    carrier === "servientrega" || carrier === "interrapidisimo";

  return (
    <Row className="mt-3">
      <Col md={12}>
        <Form.Label>Dirección de envío</Form.Label>

        <Form.Group className="mb-3">
          <Form.Label>Transportadora</Form.Label>

          <Form.Select value={carrier} onChange={handleCarrierChange} disabled={carrierLocked}>
            <option value="">Selecciona una transportadora</option>
            <option value="servientrega">Servientrega</option>
            <option value="interrapidisimo">Interrapidísimo</option>
          </Form.Select>
        </Form.Group>

        {!hasCarrier && (
          <Alert variant="secondary" className="mb-3">
            Selecciona una transportadora para continuar con la
            dirección de envío.
          </Alert>
        )}

        {isQuoting && loadingQuote && (
          <Alert variant="info" className="mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Calculando costo de envío...
          </Alert>
        )}

        {isQuoting && !loadingQuote && quoteError && (
          <Alert variant="warning" className="mb-3">
            {quoteErrorText}
          </Alert>
        )}

        {isQuoting && !loadingQuote && !quoteError && shippingCost > 0 && (
          <Alert variant="success" className="mb-3">
            Costo de envío:{" "}
            <strong>
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
                maximumFractionDigits: 0,
              }).format(shippingCost)}
            </strong>
          </Alert>
        )}

        {hasCarrier && (
          <>
            {loadingAddresses && (
              <div className="mb-3">
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Cargando direcciones...
              </div>
            )}

            {!loadingAddresses &&
              !showAddForm &&
              addresses.length > 0 &&
              addresses.map((address) => (
                <div key={address.id} className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="address"
                    id={`addr-${address.id}`}
                    value={address.id}
                    checked={selectedAddress === address.id}
                    onChange={() => handleSelectSaved(address.id)}
                  />

                  <label
                    className="form-check-label"
                    htmlFor={`addr-${address.id}`}
                  >
                    <strong>{address.name}</strong> — {address.address},{" "}
                    {address.city_id?.name},{" "}
                    {address.province_id?.name},{" "}
                    {address.country_id?.name}, CP: {address.postal_code}
                    {address.neighborhood && address.neighborhood !== "No aplica" && <> · Barrio: {address.neighborhood}</>}
                    <br />
                    <small>{address.observations}</small>
                  </label>
                </div>
              ))}

            {!loadingAddresses &&
              !showAddForm &&
              addresses.length === 0 && (
                <Alert variant="warning" className="mb-3">
                  No tienes direcciones guardadas. Ingresa una
                  dirección manualmente.
                </Alert>
              )}

            <div className="d-flex justify-content-end mt-2">
              <Button
                variant={showAddForm ? "secondary" : "outline-primary"}
                size="sm"
                onClick={handleFormToggle}
              >
                {showAddForm
                  ? "Cancelar nueva dirección"
                  : "Ingresar dirección manual"}
              </Button>
            </div>
          </>
        )}

        {showAddForm && hasCarrier && (
          <div
            className="mt-3 p-3 border rounded"
          >
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">¿Quién recibe el pedido?</Form.Label>
              <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-4">
                <Form.Check
                  type="radio"
                  id="dash-receiver-self"
                  name="dash_receiver_type"
                  label="El cliente"
                  checked={newShipping.receiver?.type !== "other"}
                  onChange={() => handleReceiverChange("type", "self")}
                />
                <Form.Check
                  type="radio"
                  id="dash-receiver-other"
                  name="dash_receiver_type"
                  label="Otra persona"
                  checked={newShipping.receiver?.type === "other"}
                  onChange={() => handleReceiverChange("type", "other")}
                />
              </div>
            </Form.Group>

            {newShipping.receiver?.type === "other" && (
              <Row>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nombre completo</Form.Label>
                    <Form.Control
                      placeholder="Ej. María Pérez"
                      value={newShipping.receiver.name}
                      onChange={(e) => handleReceiverChange("name", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Número de documento</Form.Label>
                    <Form.Control
                      placeholder="Ej. 1020304050"
                      inputMode="numeric"
                      value={newShipping.receiver.document}
                      onChange={(e) => handleReceiverChange("document", e.target.value.replace(/\D/g, ""))}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Teléfono de contacto</Form.Label>
                    <Form.Control
                      placeholder="Ej. 300 123 4567"
                      value={newShipping.receiver.phone}
                      onChange={(e) => handleReceiverChange("phone", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row>
              <Col xs={12} sm={6} lg={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Alias dirección</Form.Label>
                  <Form.Control
                    name="name"
                    placeholder="Ej. Casa, Trabajo"
                    maxLength={15}
                    value={newShipping.name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dirección</Form.Label>
                  <Form.Control
                    name="address"
                    placeholder="Ej. Cra. 34 # 11B - 70"
                    maxLength={50}
                    value={newShipping.address}
                    onChange={handleChange}
                  />
                  <Form.Text muted>{(newShipping.address || "").length} / 50 caracteres</Form.Text>
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Código postal</Form.Label>
                  <Form.Control
                    name="postal_code"
                    placeholder="Ej. 050021"
                    maxLength={10}
                    inputMode="numeric"
                    value={newShipping.postal_code}
                    onChange={(e) =>
                      handleChange({ target: { name: "postal_code", value: e.target.value.replace(/\D/g, "") } })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={12} sm={6} lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label>País</Form.Label>
                  <Form.Select name="country_id" value={newShipping.country_id} onChange={handleChange}>
                    <option value="">Selecciona</option>
                    {countries.map((country) => (
                      <option key={country.country_id} value={country.country_id}>
                        {country.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Departamento / Provincia</Form.Label>
                  <Form.Select name="province_id" value={newShipping.province_id} onChange={handleChange}>
                    <option value="">Selecciona</option>
                    {provinces.map((province) => (
                      <option key={province.id} value={province.id}>
                        {province.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ciudad</Form.Label>
                  <Form.Select name="city_id" value={newShipping.city_id} onChange={handleChange}>
                    <option value="">Selecciona</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={12} sm={6} lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Barrio / Corregimiento (opcional)</Form.Label>
                  <Form.Select
                    name="neighborhood"
                    value={newShipping.neighborhood_code || ""}
                    onChange={handleChange}
                    disabled={!newShipping.province_id || !newShipping.city_id || loadingLocalities}
                  >
                    <option value="">{loadingLocalities ? "Cargando localidades..." : "No aplica"}</option>
                    {localities.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} lg={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="observations"
                    placeholder="Ej. Frente al supermercado, portón negro"
                    value={newShipping.observations}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="mb-2 p-3 border rounded">
              <small className="text-muted d-block mb-1">Vista previa de dirección</small>
              <strong>
                {[
                  newShipping.address,
                  newShipping.neighborhood !== "No aplica" && newShipping.neighborhood,
                  newShipping.observations,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Dirección incompleta"}
              </strong>
              <div className="small text-muted">
                {[
                  countries.find((c) => c.country_id === newShipping.country_id)?.name,
                  provinces.find((p) => p.id === newShipping.province_id)?.name,
                  cities.find((c) => c.id === newShipping.city_id)?.name,
                  newShipping.postal_code,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </div>
            </div>

            {isQuoting && (
              <div className="mt-3">
                <Button
                  variant="primary"
                  onClick={handleCalculateManualShipping}
                  disabled={
                    loadingQuote ||
                    !newShipping.province_id ||
                    !newShipping.city_id
                  }
                >
                  {loadingQuote ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      Calculando...
                    </>
                  ) : (
                    "Calcular costo de envío"
                  )}
                </Button>
              </div>
            )}

            {isQuoting && !loadingQuote && quoteError && (
              <Alert variant="warning" className="mt-3 mb-0">
                {quoteErrorText}
              </Alert>
            )}

            {isQuoting &&
              !loadingQuote &&
              !quoteError &&
              shippingCost > 0 && (
                <Alert variant="success" className="mt-3 mb-0">
                  Costo de envío:{" "}
                  <strong>
                    {new Intl.NumberFormat("es-CO", {
                      style: "currency",
                      currency: "COP",
                      maximumFractionDigits: 0,
                    }).format(shippingCost)}
                  </strong>
                </Alert>
              )}
          </div>
        )}
      </Col>
    </Row>
  );
};

export default DeliveryAddressSelector;